import React, { useEffect, useMemo, useState } from 'react';

import { getClientTimeout } from '../services/clientTimeoutConfig';
import { useAppSelector } from '../store/hooks';
import { tasksApi, useGetTasksQuery } from '../store/services/tasksApi';
import { isPlanningTask, isRunningPlanningTask } from '../utils/planningTaskPresentation';

import './PlanningTasks.css';
import { Dropdown } from './Dropdown';

export { isPlanningTask, orderedPlanningTasks, planningTaskDuration } from '../utils/planningTaskPresentation';

export type TaskTypeFilter = 'all' | 'planning' | 'workspace';
const requestedPlanningRefresh = new Map<string, number>();

/** Refresh the tasks prepared by the action-invocation request before execution starts. */
export const showPlanningTasks = (projectId?: string | null): void => {
  if (projectId) {
    requestedPlanningRefresh.set(projectId, Date.now() + 30000);
    window.dispatchEvent(new globalThis.CustomEvent('planning_tasks_prepared', { detail: projectId }));
  }
  window.dispatchEvent(new Event('refresh_tasks'));
  // Preparing work only refreshes task data. The generation-start signal
  // selects the artifact being produced, without an intermediate Tasks tab.
  window.dispatchEvent(new globalThis.CustomEvent('select_task_type', { detail: 'all' }));
};

const usePlanningTaskDiscovery = (projectId?: string | null): boolean => {
  const [pollUntil, setPollUntil] = useState(() => requestedPlanningRefresh.get(projectId ?? '') ?? 0);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setPollUntil(requestedPlanningRefresh.get(projectId ?? '') ?? 0));
    const refresh = (event: Event) => {
      if ((event as globalThis.CustomEvent<string>).detail === projectId) {
        setPollUntil(requestedPlanningRefresh.get(projectId ?? '') ?? 0);
      }
    };

    window.addEventListener('planning_tasks_prepared', refresh);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('planning_tasks_prepared', refresh);
    };
  }, [projectId]);

  useEffect(() => {
    if (!pollUntil) return undefined;
    const timer = window.setTimeout(() => setPollUntil(0), Math.max(0, pollUntil - Date.now()));

    return () => window.clearTimeout(timer);
  }, [pollUntil]);

  return pollUntil > 0;
};

export const usePlanningTasks = (projectId?: string | null, enabled = true) => {
  const discoveryPolling = usePlanningTaskDiscovery(projectId);
  const querySelector = useMemo(() => tasksApi.endpoints.getTasks.select(projectId ?? ''), [projectId]);
  const hasRunningTasks = useAppSelector((state) => (
    querySelector(state).data?.some(isRunningPlanningTask) ?? false
  ));
  // The poll period comes from the admin timeout panel and is re-read on every render.
  const query = useGetTasksQuery(projectId ?? '', {
    skip: !projectId || !enabled,
    refetchOnMountOrArgChange: true,
    pollingInterval: hasRunningTasks || discoveryPolling ? getClientTimeout('planningTasksPollMs') : 0,
  });

  return { ...query, tasks: (query.currentData ?? []).filter(isPlanningTask) };
};

export const TaskTypeSelector: React.FC<{
  value: TaskTypeFilter;
  onChange: (value: TaskTypeFilter) => void;
}> = ({ value, onChange }) => (
  <Dropdown className="tasks-panel__filter" aria-label="Task type" value={value}
    onChange={(event) => onChange(event.target.value as TaskTypeFilter)}>
    <option value="all">All task types</option>
    <option value="planning">Planning tasks</option>
    <option value="workspace">Workspace tasks</option>
  </Dropdown>
);

export const useTaskTypeFilter = (projectId: string | null) => {
  const [selection, setSelection] = useState<{ projectId: string | null; type: TaskTypeFilter }>({
    projectId, type: 'all',
  });

  useEffect(() => {
    const selectType = (event: Event) => {
      const value = (event as globalThis.CustomEvent<TaskTypeFilter>).detail;

      if (['all', 'planning', 'workspace'].includes(value)) setSelection({ projectId, type: value });
    };

    window.addEventListener('select_task_type', selectType);

    return () => window.removeEventListener('select_task_type', selectType);
  }, [projectId]);

  return {
    taskType: selection.projectId === projectId ? selection.type : 'all',
    setTaskType: (type: TaskTypeFilter) => setSelection({ projectId, type }),
  };
};
